import { useCallback, useRef } from 'react';
import useFlowTrackingEvents from '../../../hooks/useFlowTrackingEvents';
import { useDynamicExperienceContext } from '../../../providers/DynamicExperienceProvider';
import { useTracking } from '../../../providers/TrackingProvider';
import { isPii } from '../../../utils/isPii';
import { createResultsEventContext } from '../../../utils/createResultsEventContext';
import { createPrqFormEventContext } from '../../../utils/createPrqEventContext';

/**
 * Custom hook that provides a debounced field input event tracker
 * Events fire after 2 seconds of inactivity per field
 */
export const useDebouncedFieldEvent = (
  productClickedCorrelationId?: string,
  dslConfigId?: string,
) => {
  const { eventContext } = useDynamicExperienceContext();
  const { triggerFieldEvents } = useFlowTrackingEvents();
  const publishedVersion = useTracking()?.stepCorrelation.dslConfigVersion;
  const dslConfigVersion = dslConfigId ? publishedVersion : undefined;

  const resultEventContext = createResultsEventContext({
    eventContext: eventContext,
    productClickedCorrelationId: productClickedCorrelationId,
    dslConfigId,
    dslConfigVersion,
  });

  // Standalone PRQ (additional-questions) form context, shared with the
  // `AdditionalQuestions` view events via `createPrqFormEventContext`. Carries
  // `dslConfigId` on the DSL path so PRQ payloads stay correlated to the
  // published config; `FieldInputted` itself still omits `customDimensions`
  // (the registered field schema rejects a top-level `customDimensions`
  // array).
  const prqEventContext = createPrqFormEventContext({
    eventContext,
    productClickedCorrelationId,
    dslConfigId,
  });

  // Store debounce timers per field using useRef to persist across renders
  const debounceTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  const sendDebouncedFieldEvent = useCallback(
    (fieldName: string, fieldType: string, value?: string, isPrq?: boolean) => {
      // Clear existing timer for this field if it exists
      if (debounceTimersRef.current[fieldName]) {
        clearTimeout(debounceTimersRef.current[fieldName]);
      }

      // Set new timer to fire event after 2 seconds of inactivity
      debounceTimersRef.current[fieldName] = setTimeout(() => {
        triggerFieldEvents('FieldInputted', {
          ...(isPrq ? prqEventContext : resultEventContext),
          fieldValue: isPii(fieldName) ? 'SANITIZED' : value || '',
          fieldName: fieldName,
          fieldType: fieldType,
        });

        // Clean up the timer reference
        delete debounceTimersRef.current[fieldName];
      }, 2000);
    },
    [eventContext, triggerFieldEvents, prqEventContext, resultEventContext],
  );

  return { sendDebouncedFieldEvent };
};
